import * as React from 'react';
import { FixMeLater } from '../../utils';
import { HoneycombShadowSize, ResponsiveStyleValue, useComponentDefaultProps } from '../../theming';
import { useStyles } from './Chip.styles';
import { Styler, StylerProps } from '../Styler';
import {
    ChipColor,
    ChipCornerStyle,
    ChipElementColors,
    ChipSize,
    ChipVariant,
} from '../../theming/types/components/Chip.types';
import { SvgIconProps } from '../SvgIcon';
import { SwitchBaseProps } from '../SwitchBase';
import { CrossIcon } from '../../icons/CrossIcon';
import { TypographyBody, TypographyBodyProps } from '../Typography';

export interface ChipProps extends StylerProps {
    /**
     * The label which will be displayed on the chip.
     */
    children?: React.ReactNode;

    /**
     * The colour of the chip.
     * @default neutral
     */
    color?: ChipColor | ChipElementColors;

    /**
     * @ignore
     */
    control?: React.ReactElement<SwitchBaseProps>;

    /**
     * If `true`, the chip will span the width of its container.
     */
    fullWidth?: boolean;

    /**
     * An svg icon component. The icon will be displayed before the label.
     */
    icon?: React.ReactElement<SvgIconProps>;

    /**
     * The size of the chip.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default m
     */
    size?: ResponsiveStyleValue<ChipSize>;

    /**
     * The style variant of the button.
     * @default filled
     */
    variant?: ChipVariant;

    /**
     * The chips's corner style. It might be preferable to set this on the theme using default props.
     * @default pill
     */
    cornerStyle?: ChipCornerStyle;

    /**
     * Callback fired when the element has been clicked.
     * @param {React.SyntheticEvent} event The event source of the callback.
     */
    onClick?: (event: React.SyntheticEvent) => void;

    /**
     * Callback fired when the component requests to be dismissed.
     * If provided, the component will be have a `dismiss` button.
     * @param {React.SyntheticEvent} event The event source of the callback.
     */
    onDismiss?: (event: React.SyntheticEvent) => void;

    /**
     * The URL that the clickable chip will navigate to. If provided, the chip will be rendered as an anchor.
     */
    href?: string;

    /**
     * Additional properties applied to the `TypographyBody` element.
     */
    LabelProps?: TypographyBodyProps;

    /**
     * A shadow from the theme.
     */
    shadow?: HoneycombShadowSize;
}

const defaultProps: Partial<ChipProps> = {
    color: 'neutral',
    size: 'm',
    variant: 'filled',
};
/**
 * The Chip component takes the given children and displays it as a chip. The chip can have many variations,
 * sizes, colors, and styles. It also supports for icons, shadows and custom behavioral definition via props. This allows us to give a complete custom behavior to this component, with the
 * ability to create chips for different use cases and scenarios. The chips can be dismissible, full width, clickable, links
 * and many other variations which gives a range of options for use.
 */
export const Chip: React.ForwardRefExoticComponent<ChipProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Chip(props: ChipProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children: label,
            color = 'neutral',
            size = 'm',
            variant = 'filled',
            cornerStyle = 'pill',
            fullWidth = false,
            icon,
            onClick,
            onDismiss,
            href,
            control,
            LabelProps,
            shadow,
            ...other
        } = useComponentDefaultProps('Chip', defaultProps, props);

        const { styles } = useStyles({
            color,
            size,
            variant,
            fullWidth,
            cornerStyle,
            shadow,
        });

        const clickable = typeof onClick === 'function' || typeof href === 'string';
        const dismissible = typeof onDismiss === 'function';

        const handleClick = (event: React.SyntheticEvent) => {
            if (onClick) {
                onClick(event);
                return;
            }
            // If the chip is dismissible but not clickable then action the dismissal.
            if (onDismiss && !control) {
                onDismiss(event);
            }
        };

        const handleDismissIconClick = (event: React.SyntheticEvent) => {
            // Stop the event from bubbling up to the `Chip`
            event.stopPropagation();
            if (onDismiss) {
                onDismiss(event);
            }
        };

        return (
            <Styler
                ref={ref}
                baseStyles={styles.root}
                button={clickable || dismissible}
                onClick={handleClick}
                href={href}
                data-clickable={clickable || undefined}
                data-chip={true}
                {...other}
            >
                {control}
                <div css={styles.surface} data-surface={true}>
                    {icon &&
                        React.cloneElement(icon, {
                            css: styles.icon,
                            'data-icon': 'true',
                        })}
                    <TypographyBody component="span" weight="inherit" {...LabelProps}>
                        {label}
                    </TypographyBody>
                    {onDismiss && <CrossIcon css={styles.dismissIcon} onClick={handleDismissIconClick} />}
                </div>
            </Styler>
        );
    }
);
